import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  CircularProgress,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
// import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import UpdateIcon from '@mui/icons-material/Update';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@/providers';
import { useNavigate } from 'react-router';

import {
  deleteRig,
  runAction,
  deployDeviceEndpoint,
  toggleMaintenance,
  stopDeviceEndpoint,
} from '../../../../../actions/rigs';
import { fetchModels } from '../../../../../actions/models';
import GridToolbar from './GridToolbar';
import STATUSES from '../../../../statuses';
import PollNode from '../PollNode';
import RIG_SERVICES from '../rigServices';
import RIG_TYPES from '../rigTypes';
import renderProgress from '../../../../shared/renderProgress';
import getHotStorage from '../getHotStorage';
import DeviceConfigsPicker from './DeviceConfigsPicker';

export default function Devices({ region, rigs }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const models = useSelector((state) => state.models);
  const user = useUser();
  const device_configs = useSelector((state) => state.deviceConfigs);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  const [transitioningRigs, setTransitioningRigs] = useState([]);
  const [deployingRigs, setDeployingRigs] = useState([]);
  const [openDeviceConfigPicker, setOpenDeviceConfigPicker] = useState(false);
  const [deviceConfigPickerRig, setDeviceConfigPickerRig] = useState(undefined);

  useEffect(() => {
    (async () => {
      if (models === null) {
        if (user?.selected_project) {
          dispatch(fetchModels(user.selected_project));
        }
        return;
      }
    })();
  }, [dispatch, models, user?.selected_project]);

  //   const viewDetails = useCallback(
  //     (rig) => () => {
  //       navigate({
  //         pathname: `/resources/region/${region.region_uuid}/node/details/${rig.rig_uuid}`,
  //         search: `?provider_type=${region.provider_type}`,
  //       });
  //     },
  //     [navigate, region]
  //   );

  useEffect(() => {
    (async () => {
      if (deployingRigs.length) {
        deployingRigs.forEach((deploying_rig_uuid) => {
          const rig = rigs.find((rig) => deploying_rig_uuid === rig.rig_uuid);
          if (
            !rig ||
            [STATUSES.RUNNING, STATUSES.FAILED].includes(rig.job_status)
          ) {
            setDeployingRigs((prevDeployingRigs) =>
              prevDeployingRigs.filter(
                (rig_uuid) => rig_uuid !== deploying_rig_uuid,
              ),
            );
          }
        });
      }
    })();
  }, [dispatch, deployingRigs, rigs]);

  const deployConfigToSelectedRigs = useCallback(async () => {
    const rig_uuids =
      rowSelectionModel.type === 'include'
        ? Array.from(rowSelectionModel.ids)
        : [];
    setDeployingRigs((prevDeployingRigs) => [
      ...prevDeployingRigs,
      ...rig_uuids,
    ]);
    setTransitioningRigs((prevTransitioningRigs) => [
      ...prevTransitioningRigs,
      ...rig_uuids,
    ]);
    await Promise.all(
      [].concat(
        rig_uuids.map(async (rig_uuid) => {
          await dispatch(
            deployDeviceEndpoint(
              region.provider_uuid,
              region.region_uuid,
              rig_uuid,
            ),
          );
        }),
      ),
    );

    setTransitioningRigs((prevTransitioningRigs) =>
      prevTransitioningRigs.filter((rig_uuid) => !rig_uuids.includes(rig_uuid)),
    );
  }, [dispatch, rowSelectionModel, region]);

  const deployLatestConfig = useCallback(
    (rig) => async () => {
      setDeployingRigs((prevDeployingRigs) => [
        ...prevDeployingRigs,
        rig.rig_uuid,
      ]);
      setTransitioningRigs((prevTransitioningRigs) => [
        ...prevTransitioningRigs,
        rig.rig_uuid,
      ]);
      await dispatch(
        deployDeviceEndpoint(
          region.provider_uuid,
          rig.region_uuid,
          rig.rig_uuid,
        ),
      );
      setTransitioningRigs((prevTransitioningRigs) =>
        prevTransitioningRigs.filter((rig_uuid) => rig_uuid !== rig.rig_uuid),
      );
    },
    [dispatch, region],
  );

  const stopEndpoint = useCallback(
    (rig) => async () => {
      setTransitioningRigs((prevTransitioningRigs) => [
        ...prevTransitioningRigs,
        rig.rig_uuid,
      ]);
      await dispatch(
        stopDeviceEndpoint(region.provider_uuid, rig.region_uuid, rig.rig_uuid),
      );
      setTransitioningRigs((prevTransitioningRigs) =>
        prevTransitioningRigs.filter((rig_uuid) => rig_uuid !== rig.rig_uuid),
      );
    },
    [dispatch, region],
  );

  const setDeviceConfig = useCallback(
    (rig) => async () => {
      setDeviceConfigPickerRig(rig);
      setOpenDeviceConfigPicker(true);
    },
    [],
  );

  const toggleNodeMaintenace = useCallback(
    (rig) => async () => {
      setTransitioningRigs((prevTransitioningRigs) => [
        ...prevTransitioningRigs,
        rig.rig_uuid,
      ]);
      await dispatch(
        toggleMaintenance(region.provider_uuid, rig.region_uuid, rig.rig_uuid),
      );
      setTransitioningRigs((prevTransitioningRigs) =>
        prevTransitioningRigs.filter((rig_uuid) => rig_uuid !== rig.rig_uuid),
      );
    },
    [dispatch, region],
  );

  const updateNodeStatistics = useCallback(
    (rig) => async () => {
      setTransitioningRigs((prevTransitioningRigs) => [
        ...prevTransitioningRigs,
        rig.rig_uuid,
      ]);
      await dispatch(
        runAction(
          region.provider_uuid,
          rig.region_uuid,
          rig.rig_uuid,
          'report',
        ),
      );
      setTransitioningRigs((prevTransitioningRigs) =>
        prevTransitioningRigs.filter((rig_uuid) => rig_uuid !== rig.rig_uuid),
      );
    },
    [dispatch, region],
  );
  const editNode = useCallback(
    (rig) => () => {
      navigate(
        `/resources/provider/${region.provider_uuid}/region/${region.region_uuid}/node/${rig.rig_uuid}/edit`,
      );
    },
    [navigate, region],
  );
  const removeNode = useCallback(
    (rig) => async () => {
      setTransitioningRigs((prevTransitioningRigs) => [
        ...prevTransitioningRigs,
        rig.rig_uuid,
      ]);
      await dispatch(
        deleteRig(region.provider_uuid, rig.region_uuid, rig.rig_uuid),
      );
      setTransitioningRigs((prevTransitioningRigs) =>
        prevTransitioningRigs.filter((rig_uuid) => rig_uuid !== rig.rig_uuid),
      );
    },
    [dispatch, region],
  );

  const renderStatus = useCallback(
    (params) => {
      if (transitioningRigs.includes(params.id)) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={20} />
          </Box>
        );
      }
      switch (params.row.status) {
        case STATUSES.ACTIVE:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <CheckCircleIcon color='success' />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
            </Box>
          );
        case STATUSES.OFFLINE:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <StopCircleIcon />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
            </Box>
          );
        case STATUSES.ERRORED:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <ErrorOutlineIcon color='error' />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
            </Box>
          );
        case STATUSES.MAINTENANCE:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <WarningIcon color='warning' />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
            </Box>
          );
        default:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <CircularProgress size={20} />
                <Typography variant='body2'>{params.row.status}</Typography>
              </Stack>
              <PollNode region={region} rig={params.row} interval={10000} />
            </Box>
          );
      }
    },
    [region, transitioningRigs],
  );

  const renderConfigStatus = useCallback(
    (params) => {
      if (transitioningRigs.includes(params.id)) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={20} />
          </Box>
        );
      }

      if (!params.row.device_config_id) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <ErrorOutlineIcon color='error' />
              <Typography variant='body2'>not set</Typography>
            </Stack>
          </Box>
        );
      }
      const device_config = device_configs
        ? device_configs.find(
            (device_config) =>
              device_config.config_id === params.row.device_config_id,
          )
        : undefined;

      if (!device_config) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <ErrorOutlineIcon color='error' />
              <Typography variant='body2'>unknown</Typography>
            </Stack>
          </Box>
        );
      }

      if (
        params.row.job_config_id === device_config.config_id &&
        params.row.job_config_revision ===
          new Date(device_config.updatedAt).valueOf().toString()
      ) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <CheckCircleIcon color='success' />
              <Typography variant='body2'>
                <b>{device_config.name}</b> last deployed{' '}
                {new Date(params.row.job_last_deployed).toLocaleDateString()}
              </Typography>
            </Stack>
          </Box>
        );
      } else {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <WarningIcon color='warning' />
              <Typography variant='body2'>
                {device_config.name} - OUT OF DATE
              </Typography>
            </Stack>
          </Box>
        );
      }
    },
    [transitioningRigs, device_configs],
  );

  const renderJobStatus = useCallback(
    (params) => {
      if (transitioningRigs.includes(params.id)) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={20} />
          </Box>
        );
      }
      if (deployingRigs.includes(params.id)) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <CircularProgress size={20} />
              <Typography variant='body2'>{params.row.job_status}</Typography>
            </Stack>
            <PollNode region={region} rig={params.row} interval={10000} />
          </Box>
        );
      }

      if (!params.row.job_status) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <ErrorOutlineIcon color='error' />
              <Typography variant='body2'>unknown</Typography>
            </Stack>
          </Box>
        );
      }
      switch (params.row.job_status) {
        case STATUSES.RUNNING:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <CheckCircleIcon color='success' />
                <Typography variant='body2'>{params.row.job_status}</Typography>
              </Stack>
            </Box>
          );

        case STATUSES.FAILED:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <ErrorOutlineIcon color='error' />
                <Typography variant='body2'>{params.row.job_status}</Typography>
              </Stack>
            </Box>
          );
        case STATUSES.STOPPED:
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <ErrorOutlineIcon color='error' />
                <Typography variant='body2'>{params.row.job_status}</Typography>
              </Stack>
            </Box>
          );
        case STATUSES.UNKNOWN:
          return (
            <Stack direction='row' spacing={1} alignItems='center'>
              <ErrorOutlineIcon color='error' />
              <Typography variant='body2'>{params.row.job_status}</Typography>
            </Stack>
          );
        default:
          return (
            <>
              <Stack direction='row' spacing={1} alignItems='center'>
                <CircularProgress size={20} />
                <Typography variant='body2'>{params.row.job_status}</Typography>
              </Stack>
              <PollNode region={region} rig={params.row} interval={10000} />
            </>
          );
      }
    },
    [region, transitioningRigs, deployingRigs],
  );

  const columns = useMemo(
    () => [
      {
        field: 'friendly_name',
        headerName: 'Name',
        type: 'string',
        flex: 0.5,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        renderCell: renderStatus,
        minWidth: 150,
        flex: 0.25,
      },
      {
        field: 'config_status',
        headerName: 'Config Status',
        type: 'string',
        renderCell: renderConfigStatus,
        minWidth: 150,
        flex: 1,
      },
      {
        field: 'job_status',
        headerName: 'Inference Status',
        type: 'string',
        renderCell: renderJobStatus,
        minWidth: 150,
        flex: 0.5,
      },
      {
        field: 'hot_storage',
        headerName: 'Storage',
        valueGetter: getHotStorage,
        renderCell: renderProgress,
        flex: 0.25,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        getActions: (params) => [
          //   <GridActionsCellItem
          //     icon={<InfoIcon />}
          //     label='View Details'
          //     onClick={viewDetails(params.row)}
          //     showInMenu
          //   />,
          <GridActionsCellItem
            icon={<SystemUpdateAltIcon />}
            label='Deploy Latest Config'
            onClick={deployLatestConfig(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<StopCircleOutlinedIcon />}
            label='Stop Device Inference'
            onClick={stopEndpoint(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<SettingsOutlinedIcon />}
            label='Set Device Config'
            onClick={setDeviceConfig(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<EngineeringIcon />}
            label='Toggle Maintenance'
            onClick={toggleNodeMaintenace(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<UpdateIcon />}
            label='Update Stats'
            onClick={updateNodeStatistics(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<EditIcon />}
            label='Edit'
            onClick={editNode(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label='Remove'
            onClick={removeNode(params.row)}
            showInMenu
          />,
        ],
      },
    ],
    [
      renderStatus,
      //   viewDetails,
      renderConfigStatus,
      renderJobStatus,
      deployLatestConfig,
      stopEndpoint,
      setDeviceConfig,
      toggleNodeMaintenace,
      updateNodeStatistics,
      editNode,
      removeNode,
    ],
  );

  const selectedRigsArray = useMemo(() => {
    if (rowSelectionModel.type === 'include') {
      return Array.from(rowSelectionModel.ids);
    } else {
      // exclude mode: all rows except those in ids
      const excludedIds = Array.from(rowSelectionModel.ids);
      return rigs
        .map((rig) => rig.rig_uuid)
        .filter((id) => !excludedIds.includes(id));
    }
  }, [rowSelectionModel, rigs]);

  const Toolbar = () => (
    <GridToolbar
      region={region}
      selected={selectedRigsArray}
      editable
      service={RIG_SERVICES.COMPUTE}
      type={RIG_TYPES.DEVICE}
      deployConfigToSelected={deployConfigToSelectedRigs}
      disabled={
        !user.cloudbender_enabled
          ? 'Enable CloudBender to add Devices'
          : undefined
      }
    />
  );

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1a-content'
        id='panel1a-header'
      >
        <Typography variant='h6' paragraph gutterBottom>
          Devices
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', height: '100%' }}>
            <div style={{ flexGrow: 1 }}>
              <DataGrid
                autoHeight
                checkboxSelection
                getRowId={(row) => row.rig_uuid}
                columns={columns}
                rows={rigs}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(newSelectionModel) => {
                  setRowSelectionModel(newSelectionModel);
                }}
                slots={{
                  toolbar: Toolbar,
                }}
                showToolbar
              />
            </div>
          </div>
        </div>
        <DeviceConfigsPicker
          region={region}
          open={openDeviceConfigPicker}
          handleClose={() => {
            setOpenDeviceConfigPicker(false);
            setDeviceConfigPickerRig(undefined);
          }}
          rig={deviceConfigPickerRig}
        />
      </AccordionDetails>
    </Accordion>
  );
}
